import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, MapPin, Clock, Check, Flag } from "lucide-react";
import { AVO, IMAGES, PRODUCTS, GALLERY } from "@/data/content";
import { MaskLine, Reveal } from "@/components/Motion";
import Marquee from "@/components/Marquee";

const ORANGE = "#F4B032";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const NavA = () => (
  <header className="absolute inset-x-0 top-0 z-40 flex items-center justify-between px-6 py-6 md:px-12">
    <button
      data-testid="concept-a-logo"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="block h-7 w-auto sm:h-9"
      aria-label="AVO Fence & Supply"
    >
      <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-full w-auto" />
    </button>
    <nav className="hidden items-center gap-8 font-mono text-[11px] uppercase tracking-[0.25em] text-[#A59E96] md:flex">
      {[
        ["Products", "products-a", "nav-link-products"],
        ["Story", "story-a", "nav-link-about"],
        ["Gallery", "gallery-a", "nav-link-gallery"],
        ["Quote", "quote-a", "nav-link-contact"],
      ].map(([label, id, tid]) => (
        <button key={id} data-testid={tid} onClick={() => scrollTo(id)} className="transition-colors hover:text-[#F1F1F1]">
          {label}
        </button>
      ))}
    </nav>
    <a
      data-testid="concept-a-phone-link"
      href={AVO.phoneHref}
      className="hidden rounded-full border border-[#2A2723] px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-[#F1F1F1] transition-colors hover:border-[#F4B032] hover:text-[#F4B032] sm:block"
    >
      {AVO.phone}
    </a>
  </header>
);

const HeroA = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.heroDark} alt="Cedar fence craftsmanship" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0C] via-[#0F0E0C]/72 to-[#0F0E0C]/55" />
        <div className="woodgrain-amber absolute inset-0" />
      </motion.div>

      <div className="relative z-10 px-6 pb-14 pt-36 md:px-12">
        <MaskLine delay={0.15}>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#F4B032] md:text-xs md:tracking-[0.35em]">
            01 — New England's Cedar Authority · Est. Stoughton, MA
          </span>
        </MaskLine>
        <h1 className="mt-6 font-condensed font-extrabold uppercase leading-[0.92] tracking-tight text-[#F1F1F1]">
          <MaskLine delay={0.3} className="text-5xl md:text-7xl lg:text-8xl">Custom Fencing.</MaskLine>
          <MaskLine delay={0.45} className="text-5xl md:text-7xl lg:text-8xl">
            <span className="text-outline-a">Craftsman</span>
          </MaskLine>
          <MaskLine delay={0.6} className="text-5xl md:text-7xl lg:text-8xl">
            Quality<span className="text-[#F4B032]">.</span>
          </MaskLine>
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <MaskLine delay={0.8}>
            <p className="max-w-md font-jakarta text-sm leading-relaxed text-[#A59E96] md:text-base">
              The largest inventory of Northern White Cedar in New England. Mill-direct fencing,
              gates, arbors, and custom post systems — crafted in Stoughton, priced without the
              middleman.
            </p>
          </MaskLine>
          <MaskLine delay={0.95}>
            <div className="flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-get-quote-button"
                onClick={() => scrollTo("quote-a")}
                className="group inline-flex items-center gap-3 rounded-full bg-[#F4B032] px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors duration-300 hover:bg-[#FFC14D]"
              >
                Get Material Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-explore-catalog-button"
                onClick={() => scrollTo("products-a")}
                className="rounded-full border border-[#2A2723] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F1F1F1] transition-colors hover:border-[#F1F1F1]"
              >
                Explore Catalog
              </button>
            </div>
          </MaskLine>
        </div>

        <Reveal delay={1.1} className="mt-14 border-t border-[#2A2723] pt-6">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              ["100%", "Northern White Cedar"],
              ["60+ Yrs", "Craftsman Heritage"],
              ["0%", "Middleman Markup"],
              ["USA", "Milled in New England"],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-condensed text-3xl font-bold text-[#F1F1F1] md:text-4xl">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ProductsA = () => (
  <section id="products-a" className="px-6 py-24 md:px-12 md:py-32">
    <Reveal>
      <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#F4B032]">02 — The Catalog</p>
      <h2 className="mt-4 max-w-3xl font-condensed text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F1F1F1] md:text-6xl">
        Built from white cedar. Stocked deep.
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-[2px] bg-[#2A2723] md:grid-cols-2 lg:grid-cols-3">
      {PRODUCTS.map((p, i) => (
        <Reveal key={p.id} delay={i * 0.06} className="bg-[#0F0E0C]">
          <article
            data-testid={`product-category-card-${p.id}`}
            className="group relative h-full bg-[#0F0E0C] p-6 transition-colors duration-500 hover:bg-[#181614]"
          >
            <div className="flex items-start justify-between">
              <span className="font-mono text-xs tracking-[0.2em] text-[#A59E96] transition-colors duration-500 group-hover:text-[#F4B032]">{String(i + 1).padStart(2, "0")}</span>
              <span className="h-2 w-2 rotate-45 bg-[#F4B032] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </div>
            <div className="relative mt-6 overflow-hidden">
              <img
                src={p.image}
                alt={p.name}
                className="h-52 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#F4B032]/45 via-[#F4B032]/10 to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-[#F4B032] transition-transform duration-700 ease-out group-hover:scale-x-100" />
            </div>
            <div className="relative mt-6">
              <h3 className="font-condensed text-2xl font-bold uppercase tracking-tight text-[#F1F1F1] transition-colors duration-500 group-hover:text-[#F4B032]">{p.name}</h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4B032]">{p.tag}</p>
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-[#F4B032] transition-[width] duration-700 ease-out group-hover:w-full" />
            </div>
            <p className="mt-4 font-jakarta text-sm leading-relaxed text-[#A59E96]">{p.description}</p>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);

const CHAPTERS = [
  {
    n: "01",
    title: "The Cedar",
    body: "Northern White Cedar is nature's fence material — naturally rot-resistant, stable, and beautiful as it silvers. We stock more of it than anyone in New England.",
  },
  {
    n: "02",
    title: "The Craft",
    body: "Every gate, arbor, and pergola is custom-designed and milled in-house. Mortise-and-tenon joinery, exact specs, no shortcuts.",
  },
  {
    n: "03",
    title: "The Supply-Only Model",
    body: "We don't install — and that's your advantage. No installation crews to feed means mill-direct pricing for installers, landscapers, and homeowners alike.",
  },
];

const StoryA = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="story-a" ref={ref} className="grid gap-12 px-6 py-24 md:grid-cols-2 md:px-12 md:py-32 lg:gap-20">
      <div className="relative overflow-hidden">
        <motion.img
          src={IMAGES.workshop}
          alt="Timber workshop"
          style={{ y: imgY }}
          className="h-[420px] w-full scale-110 object-cover md:h-full"
        />
        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#0F0E0C]/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F1F1F1] backdrop-blur">
          <Flag className="h-3.5 w-3.5 text-[#F4B032]" /> Proudly made in the USA
        </div>
      </div>
      <div>
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#F4B032]">03 — The Manifesto</p>
          <h2 className="mt-4 font-condensed text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F1F1F1] md:text-5xl">
            We supply. You build.
          </h2>
        </Reveal>
        <div className="mt-10 space-y-0 border-t border-[#2A2723]">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div className="group grid grid-cols-[64px_1fr] gap-4 border-b border-[#2A2723] py-8">
                <span className="font-condensed text-3xl font-bold text-[#2A2723] transition-colors duration-500 group-hover:text-[#F4B032]">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-condensed text-2xl font-bold uppercase tracking-tight text-[#F1F1F1]">{c.title}</h3>
                  <p className="mt-2 max-w-md font-jakarta text-sm leading-relaxed text-[#A59E96]">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-8 border-l-2 border-[#F4B032] bg-[#181614] p-6">
            <p className="font-jakarta text-sm leading-relaxed text-[#F1F1F1]">{AVO.supplyNote}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const GalleryA = () => (
  <section id="gallery-a" className="px-6 py-24 md:px-12">
    <Reveal className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#F4B032]">04 — Field Work</p>
        <h2 className="mt-4 font-condensed text-4xl font-extrabold uppercase tracking-tight text-[#F1F1F1] md:text-5xl">
          Standing across New England
        </h2>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A59E96]">Real installs · Real materials</p>
    </Reveal>
    <div className="mt-12 grid grid-cols-2 gap-[2px] bg-[#2A2723] lg:grid-cols-4">
      {GALLERY.map((g, i) => (
        <Reveal key={g.caption} delay={i * 0.08}>
          <figure className="group relative overflow-hidden bg-[#0F0E0C]">
            <img
              src={g.image}
              alt={g.caption}
              className="aspect-[3/4] w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
            />
            <figcaption className="absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-black/85 to-transparent p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F1F1F1] opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
              {g.caption}
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

const QUOTE_STEPS = ["Project", "Material", "Footage", "Contact"];
const PROJECT_TYPES = ["Residential Fence", "Gate", "Pergola / Arbor", "Custom Posts"];
const MATERIALS = ["Northern White Cedar", "Vinyl System", "Ornamental Aluminum"];

const QuoteA = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ type: "", material: "", footage: 120, name: "", contact: "", method: "Pickup" });
  const [done, setDone] = useState(false);

  const Chip = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
        active ? "border-[#F4B032] bg-[#F4B032] text-[#0F0E0C]" : "border-[#2A2723] text-[#A59E96] hover:border-[#A59E96]"
      }`}
    >
      {children}
    </button>
  );

  return (
    <section id="quote-a" className="grid gap-12 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#F4B032]">05 — Material Quote</p>
        <h2 className="mt-4 font-condensed text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F1F1F1] md:text-6xl">
          Price it before you swing a hammer.
        </h2>
        <ul className="mt-10 space-y-5">
          {["Mill-direct pricing, no middleman markup", "Pickup at the yard or scheduled delivery", "Contractor & volume accounts welcome"].map((t) => (
            <li key={t} className="flex items-center gap-3 font-jakarta text-sm text-[#A59E96]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#2A2723]">
                <Check className="h-3 w-3 text-[#F4B032]" />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="border border-[#2A2723] bg-[#181614] p-6 md:p-8">
          {done ? (
            <div data-testid="quote-success-message" className="flex min-h-[380px] flex-col items-center justify-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F4B032]">
                <Check className="h-6 w-6 text-[#0F0E0C]" />
              </span>
              <h3 className="mt-6 font-condensed text-3xl font-bold uppercase text-[#F1F1F1]">Request received</h3>
              <p className="mt-3 max-w-xs font-jakarta text-sm text-[#A59E96]">
                Our yard team will call {form.name || "you"} back with a material quote within one
                business day.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {QUOTE_STEPS.map((s, i) => (
                  <div key={s} className="flex-1">
                    <div className={`h-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-[#F4B032]" : "bg-[#2A2723]"}`} />
                    <p className={`mt-2 font-mono text-[9px] uppercase tracking-[0.2em] ${i <= step ? "text-[#F1F1F1]" : "text-[#A59E96]/50"}`}>
                      {i + 1}. {s}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 min-h-[220px]">
                {step === 0 && (
                  <div className="flex flex-wrap gap-3">
                    {PROJECT_TYPES.map((t) => (
                      <Chip key={t} active={form.type === t} onClick={() => setForm({ ...form, type: t })}>{t}</Chip>
                    ))}
                  </div>
                )}
                {step === 1 && (
                  <div className="flex flex-wrap gap-3">
                    {MATERIALS.map((m) => (
                      <Chip key={m} active={form.material === m} onClick={() => setForm({ ...form, material: m })}>{m}</Chip>
                    ))}
                  </div>
                )}
                {step === 2 && (
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A59E96]">Approximate linear footage</p>
                    <p className="mt-4 font-condensed text-6xl font-extrabold text-[#F1F1F1]">{form.footage}<span className="text-[#F4B032]">ft</span></p>
                    <input
                      data-testid="quote-footage-slider"
                      type="range" min="20" max="600" step="10" value={form.footage}
                      onChange={(e) => setForm({ ...form, footage: Number(e.target.value) })}
                      className="mt-6 w-full accent-[#F4B032]"
                    />
                    <div className="mt-4 flex gap-3">
                      {["Pickup", "Delivery"].map((m) => (
                        <Chip key={m} active={form.method === m} onClick={() => setForm({ ...form, method: m })}>{m}</Chip>
                      ))}
                    </div>
                  </div>
                )}
                {step === 3 && (
                  <div className="space-y-4">
                    <input
                      data-testid="quote-name-input"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3.5 font-jakarta text-sm text-[#F1F1F1] placeholder:text-[#A59E96]/60 focus:border-[#F4B032] focus:outline-none"
                    />
                    <input
                      data-testid="quote-contact-input"
                      placeholder="Phone or email"
                      value={form.contact}
                      onChange={(e) => setForm({ ...form, contact: e.target.value })}
                      className="w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3.5 font-jakarta text-sm text-[#F1F1F1] placeholder:text-[#A59E96]/60 focus:border-[#F4B032] focus:outline-none"
                    />
                    <p className="font-jakarta text-xs leading-relaxed text-[#A59E96]/70">
                      Summary — {form.type || "Project"} · {form.material || "Material TBD"} · {form.footage} ft · {form.method}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button
                  data-testid="quote-back-button"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A59E96] transition-colors hover:text-[#F1F1F1] disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {step < 3 ? (
                  <button
                    data-testid="quote-next-button"
                    onClick={() => setStep(step + 1)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#F1F1F1] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-white"
                  >
                    Next <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    data-testid="quote-form-submit-button"
                    onClick={() => setDone(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#F4B032] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]"
                  >
                    Request Quote <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Reveal>
    </section>
  );
};

const FooterA = () => (
  <footer className="border-t border-[#2A2723] px-6 pb-28 pt-16 md:px-12">
    <p className="font-condensed text-[13vw] font-extrabold uppercase leading-none tracking-tight text-[#181614] md:text-[9vw]">
      AVO Fence
    </p>
    <div className="mt-10 grid gap-10 md:grid-cols-3">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Visit the yard</p>
        <p className="mt-3 flex items-start gap-2 font-jakarta text-sm text-[#A59E96]">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#F4B032]" />
          {AVO.address}, {AVO.city}
        </p>
        <a href={AVO.phoneHref} className="mt-2 flex items-center gap-2 font-jakarta text-sm text-[#F1F1F1] hover:text-[#F4B032]">
          <Phone className="h-4 w-4 text-[#F4B032]" /> {AVO.phone}
        </a>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Hours</p>
        <ul className="mt-3 space-y-2">
          {AVO.hours.map((h) => (
            <li key={h.days} className="flex items-center gap-2 font-jakarta text-sm text-[#A59E96]">
              <Clock className="h-3.5 w-3.5 shrink-0 text-[#4D5548]" />
              <span className="w-32 text-[#F1F1F1]">{h.days}</span> {h.time}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">The AVO way</p>
        <p className="mt-3 font-jakarta text-sm leading-relaxed text-[#A59E96]">
          The largest inventory of quality white cedar in New England. Serving top installers,
          landscapers, and homeowners — supply only, never install.
        </p>
      </div>
    </div>
    <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-[#2A2723] pt-6">
      <img src="/avo-logo.png" alt="AVO Fence & Supply" data-testid="footer-a-logo" className="h-8 w-auto" />
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A59E96]">© 2026 AVO Fence &amp; Supply</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A59E96]">Concept A — Dark Editorial</p>
    </div>
  </footer>
);

const ConceptA = () => (
  <div data-testid="concept-a-page" className="min-h-screen bg-[#0F0E0C] pb-10">
    <div className="grain-overlay" />
    <NavA />
    <HeroA />
    <Marquee
      items={["Northern White Cedar", "Custom Arbors & Pergolas", "Contractor-Grade Gates", "Mailbox & Lamp Posts", "Made in Massachusetts", "Pro Supply Direct"]}
      className="border-y border-[#2A2723] py-5 text-[#F1F1F1]"
      itemClassName="font-condensed text-2xl font-bold uppercase tracking-wide md:text-3xl"
      speed={36}
    />
    <ProductsA />
    <StoryA />
    <GalleryA />
    <QuoteA />
    <FooterA />
  </div>
);

export default ConceptA;
